import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { nip19 } from 'nostr-tools';
import type { NPool } from '@nostrify/nostrify';
import { useAppContext } from '@/hooks/useAppContext';
import { fetchEngagement, fetchTrustSignals } from '@/lib/fetchers';
import { DEFAULT_SCAN_RELAYS, mergeRelayUrls } from '@/lib/scanRelays';
import {
  assignTickets,
  filterReasons,
  generateShuffleSeed,
  parseNoteIds,
  pickWinners,
  scoreTrust,
  validNoteIds,
  type ActivitySignals,
  type DrawOutcome,
  type EngagementResult,
  type EngagementSettings,
  type FilteredOutEntry,
  type ParsedNoteInput,
  type Participant,
  type Ticket,
  type TrustResult,
} from '@/lib/tombolo';

export type TomboloStep = 'notes' | 'rules' | 'participants' | 'filter' | 'tickets' | 'draw';

export const STEP_ORDER: TomboloStep[] = ['notes', 'rules', 'participants', 'filter', 'tickets', 'draw'];

export interface FinalizedState {
  tickets: Ticket[];
  filteredOut: FilteredOutEntry[];
  minScore: number;
  noteIds: string[];
  finalizedAt: number;
  /** Seed used for the verifiable random ticket shuffle */
  shuffleSeed: string;
}

export function useTombolo() {
  const { nostr } = useNostr();
  const { config } = useAppContext();

  // --- Step navigation ---
  const [step, setStep] = useState<TomboloStep>('notes');

  // --- Step 1: note input ---
  const [noteInput, setNoteInput] = useState('');
  const parsed: ParsedNoteInput[] = useMemo(() => parseNoteIds(noteInput), [noteInput]);
  const noteIds = useMemo(() => validNoteIds(parsed), [parsed]);

  const removeInvalid = useCallback(() => {
    const kept = parseNoteIds(noteInput)
      .filter((p) => p.status === 'valid')
      .map((p) => p.raw);
    setNoteInput(kept.join('\n'));
  }, [noteInput]);

  // --- Step 2: engagement settings ---
  const [settings, setSettings] = useState<EngagementSettings>({
    likes: true,
    reposts: true,
    comments: true,
  });

  // --- Step 3/4: fetch engagement ---
  const [isFetching, setIsFetching] = useState(false);
  const [fetchProgress, setFetchProgress] = useState('');
  const [engagement, setEngagement] = useState<EngagementResult | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Scan a wide set of well-known relays plus the user's own read relays,
  // so engagement stored on only some relays isn't missed.
  const relayUrls = useMemo(
    () => mergeRelayUrls(
      DEFAULT_SCAN_RELAYS,
      config.relayMetadata.relays.filter((r) => r.read).map((r) => r.url),
    ),
    [config.relayMetadata],
  );

  const runFetch = useCallback(async () => {
    if (noteIds.length === 0) return;
    setIsFetching(true);
    setFetchError(null);
    setEngagement(null);
    setSignals(null);
    try {
      const result = await fetchEngagement(
        nostr as NPool,
        relayUrls,
        noteIds,
        settings,
        setFetchProgress,
      );
      if (result.okRelays.length === 0) {
        setFetchError('None of the relays responded. Please check your connection and try again.');
      } else {
        setEngagement(result);
        setStep('participants');
      }
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Something went wrong while fetching.');
    } finally {
      setIsFetching(false);
      setFetchProgress('');
    }
  }, [nostr, relayUrls, noteIds, settings]);

  // --- Step 5: WOT / anti-bot filter ---
  const [minScore, setMinScore] = useState(30);
  const [seedInput, setSeedInput] = useState('');
  const [isScoring, setIsScoring] = useState(false);
  const [scoringProgress, setScoringProgress] = useState('');
  const [signals, setSignals] = useState<Record<string, ActivitySignals> | null>(null);

  const seedPubkeys = useMemo(() => {
    const out: string[] = [];
    for (const token of seedInput.split(/[\s,;]+/)) {
      const t = token.trim().replace(/^nostr:/i, '');
      if (!t) continue;
      if (/^[0-9a-fA-F]{64}$/.test(t)) {
        out.push(t.toLowerCase());
        continue;
      }
      try {
        const decoded = nip19.decode(t);
        if (decoded.type === 'npub') out.push(decoded.data);
        else if (decoded.type === 'nprofile') out.push(decoded.data.pubkey);
      } catch {
        // ignore invalid seed entries
      }
    }
    return [...new Set(out)];
  }, [seedInput]);

  const runScoring = useCallback(async () => {
    if (!engagement) return;
    setIsScoring(true);
    try {
      const result = await fetchTrustSignals(
        nostr as NPool,
        relayUrls,
        engagement.participants.map((p) => p.pubkey),
        engagement.engagementEventIds,
        seedPubkeys,
        setScoringProgress,
      );
      setSignals(result);
    } finally {
      setIsScoring(false);
      setScoringProgress('');
    }
  }, [nostr, relayUrls, engagement, seedPubkeys]);

  const trustResults: Record<string, TrustResult> = useMemo(() => {
    if (!engagement || !signals) return {};
    const out: Record<string, TrustResult> = {};
    for (const p of engagement.participants) {
      out[p.pubkey] = scoreTrust(p.pubkey, engagement.profiles[p.pubkey], signals[p.pubkey]);
    }
    return out;
  }, [engagement, signals]);

  const { eligible, filteredOut } = useMemo(() => {
    const eligible: { participant: Participant; trust: TrustResult }[] = [];
    const filteredOut: FilteredOutEntry[] = [];
    if (engagement && signals) {
      for (const p of engagement.participants) {
        const trust = trustResults[p.pubkey];
        if (!trust) continue;
        if (trust.score >= minScore) {
          eligible.push({ participant: p, trust });
        } else {
          filteredOut.push({ participant: p, trust, reasons: filterReasons(trust, minScore) });
        }
      }
    }
    return { eligible, filteredOut };
  }, [engagement, signals, trustResults, minScore]);

  // --- Step 6: preview tickets (before finalization) ---
  // A random seed is generated once per raffle. Ticket order is a seeded
  // shuffle: SHA-256(seed + pubkey), so it's unpredictable before the seed
  // exists but fully verifiable once the seed is published.
  const [shuffleSeed, setShuffleSeed] = useState<string>(() => generateShuffleSeed());
  const [previewTickets, setPreviewTickets] = useState<Ticket[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (eligible.length === 0) {
      setPreviewTickets([]);
      return;
    }
    assignTickets(eligible, shuffleSeed).then((tickets) => {
      if (!cancelled) setPreviewTickets(tickets);
    });
    return () => { cancelled = true; };
  }, [eligible, shuffleSeed]);

  // --- Step 7: finalize ---
  const [finalized, setFinalized] = useState<FinalizedState | null>(null);

  const finalizeTickets = useCallback(() => {
    if (previewTickets.length === 0) return;
    setFinalized({
      tickets: previewTickets,
      filteredOut,
      minScore,
      noteIds,
      finalizedAt: Math.floor(Date.now() / 1000),
      shuffleSeed,
    });
    setStep('draw');
  }, [previewTickets, filteredOut, minScore, noteIds, shuffleSeed]);

  // --- Step 8/9: draw ---
  const [drawnInput, setDrawnInputRaw] = useState('');
  const [outcome, setOutcome] = useState<DrawOutcome | null>(null);
  const [drawAt, setDrawAt] = useState<number | null>(null);

  // Changing the drawn number always clears the previous result, so a stale
  // winner can never be mistaken for a new draw.
  const setDrawnInput = useCallback((value: string) => {
    setDrawnInputRaw(value);
    setOutcome(null);
    setDrawAt(null);
  }, []);

  const runDraw = useCallback(() => {
    if (!finalized) return;
    const num = Number(drawnInput.trim());
    // The drawn number must be a whole number within the ticket range.
    // Numbers outside 1..N would ALWAYS pick the first or last ticket,
    // which would make the draw unfair.
    if (!Number.isInteger(num)) return;
    if (num < 1 || num > finalized.tickets.length) return;
    const result = pickWinners(finalized.tickets, num);
    if (result) {
      setOutcome(result);
      setDrawAt(Math.floor(Date.now() / 1000));
    }
  }, [finalized, drawnInput]);

  // --- Reset ---
  const resetAll = useCallback(() => {
    setStep('notes');
    setNoteInput('');
    setSettings({ likes: true, reposts: true, comments: true });
    setEngagement(null);
    setFetchError(null);
    setSignals(null);
    setMinScore(30);
    setSeedInput('');
    setShuffleSeed(generateShuffleSeed());
    setFinalized(null);
    setDrawnInput('');
    setOutcome(null);
    setDrawAt(null);
  }, [setDrawnInput]);

  const isLocked = finalized !== null;

  return {
    // navigation
    step, setStep, isLocked,
    // step 1
    noteInput, setNoteInput, parsed, noteIds, removeInvalid,
    // step 2
    settings, setSettings,
    // step 3/4
    isFetching, fetchProgress, engagement, fetchError, runFetch, relayUrls,
    // step 5
    minScore, setMinScore, seedInput, setSeedInput, seedPubkeys,
    isScoring, scoringProgress, signals, runScoring,
    trustResults, eligible, filteredOut,
    // step 6/7
    previewTickets, shuffleSeed, finalized, finalizeTickets,
    // step 8/9
    drawnInput, setDrawnInput, outcome, drawAt, runDraw,
    // reset
    resetAll,
  };
}

export type TomboloState = ReturnType<typeof useTombolo>;
